class ProductAbout extends HTMLElement {}

if (!customElements.get('product-about')) {
  customElements.define('product-about', ProductAbout);
}

/**
 * Scroll-driven hero image animation (desktop only).
 *
 * Two modes:
 *   1. Default (boots/gear): image starts in the gallery hero area and
 *      continuously interpolates toward its final position in the about
 *      section as the user scrolls — no threshold snaps or timed transitions.
 *      AAS-5056: on the default/accessory template the interpolation is
 *      vertical-only (no horizontal flight, no zoom, no spring) and the hero
 *      re-joins the flow at the About section's bottom edge.
 *   2. Apparel (scroll_triptych): image is HIDDEN until the about section
 *      reaches the viewport, then appears and stays hard-locked within the
 *      About/Datasheet/Materials region — no transitions, no overshoot.
 */
(() => {
  const DESKTOP_BP = 1025;
  const IMAGE_ASPECT_RATIO = 716 / 715;
  const INITIAL_HEIGHT_VH = 0.84;
  const INITIAL_TOP = 105;
  /* Floor for the panel-morph (ski/boot/protection) initial frame only. Its
     SKU-anchored value put the ski tips at ~90px, overlapping the floating
     breadcrumb (text at ~96-111) — invisible with a short trail, which sits
     clear to the left, but a long title runs under the image, e.g. "CLOUD C11
     REVOSHOCK LIGHT + M 10 GW" intersecting the tips. Deliberately NOT
     INITIAL_TOP: apparel keeps its original 105 resting position. AAS-4279 */
  const MORPH_MIN_TOP = 125;
  const FINAL_TOP = 40;
  const FINAL_HEIGHT_VH = 1.25;
  /* Boot-family final frame cap within the right viewport half — boot,
     protection and the AAS-5117 apparel triptych-fallback. Despite the name
     it is NOT the ski path: ski takes the SKI_ZOOM_* frame above, and the
     AAS-5056 default/accessory template returns its own parked frame before
     this constant is read. */
  const SKI_FINAL_HEIGHT_VH = 0.6;
  /* AAS-5168: the About packshot renders at 70% of the shared final frame —
   * the full-size frame crowded the Story column. AAS-5466 reuses it so the
   * apparel triptych-fallback lands on the same size as the real apparel
   * template instead of the larger boot frame it inherited. */
  const APPAREL_SCALE = 0.7;
  /* Ski final frame (AAS-5075): zoom-in in place of the old left→right
   * travel. The image stays CONTAINED (no crop): target height is 2
   * viewport heights, and when that would push the width past 40vw the box
   * scales down to the 40vw cap with the height following the aspect.
   * Sized off the image's REAL natural aspect — ski cutouts are tall and
   * narrow, nothing like the square IMAGE_ASPECT_RATIO constant the boot
   * frames assume — so the width cap only bites on genuinely narrow
   * windows. */
  const SKI_ZOOM_HEIGHT_VH = 2;
  const SKI_ZOOM_MAX_WIDTH_VW = 0.4;
  /* Ski initial frame: 90% of the SKU→gutter band, centered in it
   * (previously filled the band edge-to-edge). AAS-5075 */
  const SKI_INITIAL_SCALE = 0.9;
  /* Ski phase 2 (AAS-5075): once the zoom completes, the image un-pins and
   * scrolls 1:1 with the page; the sections after the story arrive only
   * after its bottom edge has passed, plus this whitespace. The story
   * section's min-height is sized by JS (updateSkiScrollRoom) to give the
   * document exactly that much scroll room. */
  const SKI_SCROLL_END_PAD = 120;
  /* The story copy pins CENTRED in the viewport: the pin top is derived per
   * frame from the layout's own height ((100vh − height) / 2, floored at
   * this minimum), so short and long copy alike hold the vertical centre.
   * (JS-driven soft-sticky — see updateStoryPin; native position:sticky was
   * dropped because its pin/release corners are hard velocity steps.)
   * AAS-5075 */
  const SKI_STORY_MIN_TOP = 24;
  /* Corner rounding for the soft-sticky smooth-max/min (px). Bigger =
   * softer, longer ease into/out of the pin. AAS-5075 */
  const SKI_STORY_SOFT_PX = 120;
  /* Up-scroll exit: the pinned copy sits well above its flow position, so a
   * plain 1:1 ride could never leave the viewport (at page top it would
   * float near the fold and the reveal could never re-arm). Instead the
   * frozen offset decays to zero over this much upward scroll — the copy
   * exits a bit faster than the page (~2×) and lands back in flow, safely
   * below the fold. AAS-5075 */
  const SKI_STORY_EXIT_DECAY_PX = 400;
  /* Phase 1→2 blend: the first N scrolled pixels past the morph end ramp
   * the image's 1:1 follow in quadratically (velocity 0→1, C1-continuous)
   * instead of snapping. Costs a constant N/2 of scroll distance, which
   * updateSkiScrollRoom compensates. AAS-5075 */
  const SKI_PHASE2_BLEND_PX = 240;
  /* Boot initial frame: cap within the SKU→gutter band (the near-square boot
   * cutout filling the full band read as huge); the capped box centers
   * vertically in the band. Ski keeps the full band. */
  const BOOT_INITIAL_MAX_HEIGHT_VH = 0.65;
  const FINAL_WIDTH_FALLBACK = 820;
  const FINAL_HEIGHT_FALLBACK = 818;

  const EDGE_GUTTER = 24;
  const ABOUT_CONTENT_GAP = 60;
  const BOTTOM_LOCK_HYSTERESIS = 6;
  /* Clearance kept between the bottom-locked hero and the bottom of the About
   * region, so the morphed-in product shot never butts up against the next
   * section. Only applied for the reserved (empty boot/ski) About placeholder —
   * populated PDPs keep their original flush lock. */
  const RESERVED_BOTTOM_GAP = 96;

  // Teardown for the previous init() — aborted on re-init so the scroll/resize
  // listeners bound below never stack across variant swaps.
  let _teardown = null;
  // The exact node the current run manages. Used to skip a redundant re-init when
  // the node survived a variant swap untouched (theme.js preserves the product-
  // level apparel cutout). Re-initing a surviving node would abort its live
  // listeners and re-apply the transform → a visible jump/flash on variant change.
  let _managedNode = null;

  function init() {
    const img = document.querySelector('.product__single-image');
    const innerImg = document.querySelector('.product__single-image-img');

    // No-op if the same node is still managed (preserved across the swap). Only
    // re-init when the node was actually recreated (ski/boot/accessory layouts,
    // whose cutout nests the variant-aware overlay, so theme.js does NOT preserve
    // it). This is what makes variant changes flash-free on apparel.
    if (img && img === _managedNode && img.isConnected) return;

    // The #ProductMedia wrapper (which contains .product__single-image) can be
    // replaced wholesale on variant change (renderProductInfo). When that recreates
    // the node, re-run init() on the new one. Tie every listener to an
    // AbortController and abort the prior run first — no duplicate handlers / stale
    // closures.
    if (_teardown) _teardown();
    const ac = new AbortController();
    const signal = ac.signal;
    _teardown = () => ac.abort();
    _managedNode = img;
    const about = document.querySelector('[data-about-section]');
    const aboutContent = about?.querySelector('.product-about__content');
    const sidebarWidth = 419;

    const regionSections = document.querySelectorAll('[data-about-region]');

    const isApparel = !!document.querySelector('.product--scroll_triptych');
    /* AAS-1179 panel-morph choreography — ski, boot AND protection (boots
     * share the full ski behavior: SKU-anchored initial frame, spring follow
     * from the first scrolled pixel, right-half final frame completing at
     * panel-away, then parked and covered by the following sections).
     * Protection rides the boot variant (AAS-4173): its packshots are
     * near-square like boots, so it shares the capped initial height too.
     * AAS-4918: the default/accessory template (template-product-accessory)
     * rides boot too — its packshots are contained/near-square and its hero
     * sizing vars are already the boot-like capped set, so the capped initial
     * height is the right fit.
     * AAS-5117: the apparel triptych single-image FALLBACK (imagery-poor
     * apparel — one GHO packshot, no model shots) rides boot as well: its
     * packshot is the same near-square cutout, and the legacy default-mode
     * 84vh frame it fell into read as huge. The body still carries
     * template-product-apparel, so the fallback is detected off the
     * product--triptych-fallback layout modifier (main-product.liquid); the
     * REAL triptych never renders that class and keeps apparel mode. */
    const isBootLike =
      document.body.classList.contains('template-product-boot') ||
      document.body.classList.contains('template-product-protection') ||
      document.body.classList.contains('template-product-accessory') ||
      (document.body.classList.contains('template-product-apparel') &&
        !!document.querySelector('.product--triptych-fallback'));
    /* AAS-5056: the default/accessory template rides the boot panel-morph but
     * WITHOUT the horizontal flight — the hero keeps its column and only
     * travels vertically into the About landing (see getFinalFrame).
     * Deliberately NOT extended to the AAS-5117 apparel triptych-fallback:
     * that fallback is meant to ride the full boot treatment (horizontal
     * flight into the right half), and its About is the apparel layout, not
     * the accessory template's right-aligned 270px column. */
    const isDefaultTpl = document.body.classList.contains('template-product-accessory');
    /* AAS-5466: the apparel triptych-fallback keeps the boot two-phase flight
     * (left, then across into the right half) but must match the real apparel
     * template in the two places the boot treatment got wrong for it: the final
     * frame size, and re-joining the flow at the end of the About block instead
     * of staying pinned over the datasheet. */
    const isApparelFallback =
      document.body.classList.contains('template-product-apparel') &&
      !!document.querySelector('.product--triptych-fallback');
    const isSki = document.body.classList.contains('template-product-ski');
    const usesPanelMorph = isSki || isBootLike;
    const isReservedAbout = !!(about && about.classList.contains('product-about-section--reserved'));

    /* (AAS-4918's aboutCopyOnRight static fork is gone — it existed because
     * the old accessory morph flew the hero horizontally into the template's
     * right-aligned About copy. The AAS-5056 default-template morph is
     * vertical-only in the LEFT column, so populated and reserved About both
     * ride the same clamped path below.) */

    /* ── Ski initial frame ─────────────────────────────────────────────
     * The image sits centered in the free area left of the sidebar, its top
     * aligned with the sidebar content start (.product__sku--inline) and its
     * bottom at the viewport bottom minus EDGE_GUTTER. The sku anchor is
     * cached in document coords (its viewport position at scroll top) and
     * refreshed on resize/load/pageshow — measuring live per frame would
     * shift the t=0 reference while scrolling. */
    const skuAnchor = document.querySelector('.product__sku--inline');
    let skiInitialTop = null;
    // Right edge of the free area = the side panel's real rendered left edge
    // (the 419px shell constant overstates it — panel is narrower in practice,
    // which made the image read as centered in the full viewport).
    let skiAreaRight = null;
    // Panel bottom in document coords: the ski morph completes exactly when
    // this scrolls past the viewport top (panel fully scrolled away).
    let skiPanelBottom = null;

    function refreshSkiMetrics() {
      if (!usesPanelMorph || !skuAnchor) {
        skiInitialTop = null;
        skiAreaRight = null;
        skiPanelBottom = null;
        return;
      }
      // Direct document-coord measurement. The sku's sticky ancestor is
      // vertically inert today (top: auto), so the naive measure is correct;
      // the guard below keeps the cached anchor if a future breakpoint makes
      // the sticky actually engage (rect pinned at its CSS top mid-scroll —
      // measuring then would bake the stuck position into the anchor).
      const sticky  = skuAnchor.closest('.product__column-sticky');
      const wrapper = skuAnchor.closest('.product__info-wrapper');
      if (sticky && window.scrollY > 1 && skiInitialTop !== null) {
        const stickyCssTop = getComputedStyle(sticky).top;
        const isStuck =
          stickyCssTop !== 'auto' &&
          Math.abs(sticky.getBoundingClientRect().top - parseFloat(stickyCssTop)) < 1;
        if (isStuck) return;
      }
      skiInitialTop = Math.max(
        0,
        skuAnchor.getBoundingClientRect().top + window.scrollY
      );
      // Free-area right edge = the VISIBLE panel column (title/settings),
      // not the flex wrapper: the wrapper spans a full 50% of the viewport,
      // with the 270px content column centered inside it — measuring the
      // wrapper put the centering area far left of what the eye sees.
      const infoColumn = wrapper?.querySelector('.product__info-container') || wrapper;
      const columnRect = infoColumn?.getBoundingClientRect();
      skiAreaRight = columnRect && columnRect.left > 0 ? columnRect.left : null;
      skiPanelBottom = columnRect ? columnRect.bottom + window.scrollY : null;
    }
    refreshSkiMetrics();

    /* The vertical band the initial frame lives in: SKU line down to the
     * bottom gutter. Ski takes 90% of it, centered (AAS-5075); boot caps
     * its height inside it and centers (the near-square boot filling the
     * whole band read as huge). */
    function getInitialBand() {
      const band = Math.max(
        300,
        window.innerHeight - EDGE_GUTTER - skiInitialTop
      );
      const height = isBootLike
        ? Math.min(band, window.innerHeight * BOOT_INITIAL_MAX_HEIGHT_VH)
        : band * SKI_INITIAL_SCALE;
      return { band, height };
    }

    function getInitialTopValue() {
      if (!usesPanelMorph) return INITIAL_TOP;
      if (skiInitialTop !== null) {
        const { band, height } = getInitialBand();
        /* Clamp only — it bites when the SKU block sits high enough to pull the
           frame under the breadcrumb; otherwise the SKU anchoring the morph
           depends on is untouched. See MORPH_MIN_TOP. */
        return Math.max(MORPH_MIN_TOP, skiInitialTop + (band - height) / 2);
      }
      // Fallback (sku block absent): bottom-anchor the 84vh frame as before.
      const initialHeight = window.innerHeight * INITIAL_HEIGHT_VH;
      return Math.max(0, window.innerHeight - initialHeight);
    }

    /* Ski (AAS-5075): give the document the scroll room for phase 2. At the
     * moment the morph completes (scrollY == skiPanelBottom) the next
     * section's top must sit SKI_SCROLL_END_PAD below the zoomed image's
     * bottom — from then on image and page both move 1:1, so the page
     * "continues" right after the image runs out. The story section's
     * min-height places its bottom (= where the following sections start)
     * exactly there. Re-run on resize/settle — the target height depends on
     * the viewport and the hero's decoded aspect. */
    function updateSkiScrollRoom() {
      if (!isSki || !about) return;
      if (window.innerWidth < DESKTOP_BP || skiInitialTop === null || skiPanelBottom === null) {
        about.style.height = '';
        return;
      }
      const finalFrame = getFinalFrame();
      /* The image's bottom edge behaves like a document point at
       * skiPanelBottom + initialTop + height once phase 2 has it moving
       * with the page (plus the constant blend/2 the quadratic phase-2
       * ramp leaves behind — see applyAtProgress). The story copy's
       * release needs no sizing here: the soft-sticky (updateStoryPin)
       * clamps against the image's live rendered bottom each frame. */
      const imageBottomDoc =
        skiPanelBottom + getInitialTopValue() + finalFrame.height + SKI_PHASE2_BLEND_PX / 2;
      const aboutTopDoc = about.getBoundingClientRect().top + window.scrollY;
      const targetHeight = imageBottomDoc + SKI_SCROLL_END_PAD - aboutTopDoc;
      about.style.height = targetHeight > 0 ? `${Math.ceil(targetHeight)}px` : '';
    }

    function getRegionRect() {
      const aboutRect = about.getBoundingClientRect();
      if (regionSections.length <= 1) return aboutRect;

      const lastSection = regionSections[regionSections.length - 1];
      const lastRect = lastSection.getBoundingClientRect();

      return {
        top: aboutRect.top,
        bottom: lastRect.bottom,
        left: aboutRect.left,
        right: aboutRect.right,
        width: aboutRect.width,
        height: lastRect.bottom - aboutRect.top
      };
    }

    if (!img) return;

    /* Safety net: the hero (.product__single-image) is position: fixed via CSS
     * and is ONLY un-pinned/animated below when an About section is present to
     * drive the scroll morph. If the fixed hero rendered without a
     * [data-about-section] (e.g. a template that omits product-about), clearing
     * the fixed positioning prevents the image from staying pinned in the
     * viewport for the whole page. */
    if (!about) {
      img.style.position = 'static';
      return;
    }

    if (window.innerWidth < DESKTOP_BP) {
      img.style.display = 'none';
      // Drop stale desktop pins (AAS-5075) — without this a breakpoint
      // crossing leaves the mobile story section thousands of pixels tall
      // (and the copy pinned mid-air).
      if (isSki) {
        about.style.height = '';
        const layout = about.querySelector('.product-about__layout');
        if (layout) {
          layout.style.transform = '';
          layout.style.position = '';
          layout.style.top = '';
          layout.style.left = '';
          layout.style.width = '';
        }
      }
      return;
    }

    /* Static-hero path — the merchant opt-out (disable_gallery_scroll →
     * [data-no-image-scroll]) parks the image without a morph. */
    if (document.querySelector('[data-no-image-scroll]')) {
      const updateAccessoryPosition = () => {
        if (window.innerWidth < DESKTOP_BP) {
          img.style.top = '';
          return;
        }
        const imageHeight = INITIAL_HEIGHT_VH * window.innerHeight;
        const regionRect = getRegionRect();
        const topY = Math.min(INITIAL_TOP, regionRect.bottom - imageHeight);
        img.style.top = `${topY}px`;
      };

      let accRaf = null;
      window.addEventListener('scroll', () => {
        if (!accRaf) {
          accRaf = requestAnimationFrame(() => {
            updateAccessoryPosition();
            accRaf = null;
          });
        }
      }, { passive: true, signal });
      window.addEventListener('resize', updateAccessoryPosition, { signal });
      window.addEventListener('load', updateAccessoryPosition, { signal });
      window.addEventListener('pageshow', updateAccessoryPosition, { signal });
      updateAccessoryPosition();
      return;
    }

    img.style.left = '0px';
    img.style.top = '0px';
    img.style.transformOrigin = 'top left';

    /* Final (morphed-in) frame: the image centered in the whitespace to the
     * right of the About content column, clamped to fit. Shared by the
     * apparel and default paths (they were two identical copies before).
     * Boot family overrides the target: the right half of the viewport,
     * box centered in that half, height capped at 60vh, vertically centered
     * (returns y; the other paths fall back to FINAL_TOP in the caller).
     * Ski (AAS-5075) zooms IN PLACE instead: the box keeps the initial
     * frame's center axis (no horizontal travel) and grows toward
     * SKI_ZOOM_HEIGHT_VH viewports, contained — the 40vw width cap scales
     * the whole box down (height follows the aspect), never crops. */
    function getFinalFrame() {
      if (isSki && skiInitialTop !== null) {
        /* Real packshot aspect (fit=bounds keeps the tall narrow cutout).
         * Falls back to the square constant until the image has decoded —
         * the frame is re-derived on every scroll frame, so the real
         * aspect takes over as soon as naturalWidth lands. */
        const aspect = innerImg && innerImg.naturalWidth > 0
          ? innerImg.naturalWidth / innerImg.naturalHeight
          : IMAGE_ASPECT_RATIO;
        let height = window.innerHeight * SKI_ZOOM_HEIGHT_VH;
        let width = height * aspect;
        const maxWidth = window.innerWidth * SKI_ZOOM_MAX_WIDTH_VW;
        if (width > maxWidth) {
          width = maxWidth;
          height = width / aspect;
        }
        const { width: initialWidth } = getInitialDimensions();
        const centerX = getResponsiveInitialLeft() + initialWidth / 2;
        return {
          x: centerX - width / 2,
          /* Top-anchored zoom (AAS-5075): the top edge holds the initial
           * frame's position for the whole morph — the image only grows
           * downward past the fold; phase 2 then scrolls it through. */
          y: getInitialTopValue(),
          width,
          height,
        };
      }
      if (usesPanelMorph && skiInitialTop !== null) {
        const half = window.innerWidth / 2;
        let height = window.innerHeight * SKI_FINAL_HEIGHT_VH;
        let width = height * IMAGE_ASPECT_RATIO;
        if (width > half) {
          width = half;
          height = width / IMAGE_ASPECT_RATIO;
        }
        /* AAS-5056: default/accessory template — no horizontal flight and no
         * zoom. The final frame IS the initial box (same x, same size); the
         * morph reduces to a purely vertical glide into the parked frame,
         * vertically centred in the viewport. */
        if (isDefaultTpl) {
          const { width: iw, height: ih } = getInitialDimensions();
          return {
            x: getResponsiveInitialLeft(),
            y: (window.innerHeight - ih) / 2,
            width: iw,
            height: ih,
          };
        }
        /* AAS-5466: match the real apparel template's packshot 1:1 — same
         * base, same scale, same anchor. The first cut scaled the ski frame
         * (0.6·vh, viewport-half cap) by APPAREL_SCALE, but the real template
         * scales the WHITESPACE frame (getWhitespaceFinalFrame, bounded by
         * vh−2·gutter) — a different, larger base, so the fallback landed
         * ~30% smaller than a real apparel PDP at the same viewport. Mirror
         * apparelUpdate exactly: whitespace frame × APPAREL_SCALE, re-centred
         * in the full frame's slot. y stays viewport-centred: the real
         * template FADES IN pinned at FINAL_TOP, but the fallback FLIES here
         * — a FINAL_TOP target sent the whole flight gliding upward. */
        if (isApparelFallback) {
          const full = getWhitespaceFinalFrame();
          const scaledWidth = full.width * APPAREL_SCALE;
          const scaledHeight = full.height * APPAREL_SCALE;
          return {
            x: full.x + (full.width - scaledWidth) / 2,
            y: (window.innerHeight - scaledHeight) / 2,
            width: scaledWidth,
            height: scaledHeight,
          };
        }
        return {
          x: half + (half - width) / 2,
          y: (window.innerHeight - height) / 2,
          width,
          height,
        };
      }

      return getWhitespaceFinalFrame();
    }

    /* The classic final frame: the image centered in the whitespace to the
     * right of the About content column, clamped to fit. The direct target
     * for non-panel-morph templates, and the shared BASE for both apparel
     * paths — apparelUpdate (real template) and the triptych-fallback branch
     * above both render at APPAREL_SCALE of this frame (AAS-5466). */
    function getWhitespaceFinalFrame() {
      const { width, height } = getBaseFinalDimensions();
      const viewportWidth = window.innerWidth;
      const contentRect = aboutContent?.getBoundingClientRect();
      let contentRight;

      if (contentRect && contentRect.width > 0) {
        contentRight = contentRect.right;
      } else {
        // Content not measurable yet — reconstruct its right edge from the
        // layout skeleton (container padding + label + gap + content width).
        const container = about?.querySelector('.product-about-section__inner');
        const layout = about?.querySelector('.product-about__layout');
        const label = about?.querySelector('.product-about__label');
        let containerLeft = EDGE_GUTTER;

        if (container) {
          const containerRect = container.getBoundingClientRect();
          const paddingLeft = parseFloat(window.getComputedStyle(container).paddingLeft) || 0;
          containerLeft = containerRect.left + paddingLeft;
        }

        if (layout && label && aboutContent) {
          const layoutGap = parseFloat(window.getComputedStyle(layout).gap) || 0;
          const labelRect = label.getBoundingClientRect();
          const contentStyles = window.getComputedStyle(aboutContent);
          const contentMaxWidth = parseFloat(contentStyles.maxWidth) || aboutContent.getBoundingClientRect().width;
          contentRight = containerLeft + labelRect.width + layoutGap + contentMaxWidth;
        } else {
          contentRight = containerLeft + 512;
        }
      }

      const whitespaceStart = contentRight + EDGE_GUTTER;
      const whitespaceEnd = viewportWidth - EDGE_GUTTER;
      const whitespaceWidth = whitespaceEnd - whitespaceStart;
      const clampedWidth = Math.min(width, whitespaceWidth);
      const clampedHeight = clampedWidth < width
        ? clampedWidth / IMAGE_ASPECT_RATIO
        : height;
      const x = whitespaceStart + (whitespaceWidth - clampedWidth) / 2;

      return { x, width: clampedWidth, height: clampedHeight };
    }

    /* ================================================================
     * APPAREL MODE — completely separate, simple path.
     * The image is hidden by default. When the region is in view,
     * position is hard-clamped on every scroll frame with zero
     * transitions. Only opacity gets a short fade.
     * ================================================================ */
    if (isApparel) {
      img.style.opacity = '0';
      img.style.pointerEvents = 'none';
      img.style.transition = 'opacity 350ms ease';
      let apparelRaf = null;
      let apparelVisible = false;

      /* AAS-5168: the About packshot renders at 70% of the shared final
         frame (the full-size frame crowded the Story column), re-centered
         in the whitespace getFinalFrame computed for the full width.
         APPAREL_SCALE is module-level since AAS-5466 — the triptych-fallback
         needs the same number from the default-mode path. */

      /* AAS-5168: jitter-free ride. Re-positioning a fixed element from a
         scroll-driven rAF always trails the compositor's scroll, so the two
         travelling phases (entering aligned with the Story copy top,
         exiting pushed up by the region bottom) visibly wobbled. Their
         target positions are constant in DOCUMENT coordinates, so anchor
         them once with position:absolute and let native scrolling carry
         the image; only the truly stationary middle phase stays fixed.
         Mode boundaries are geometrically coincident — no jump. */
      let apparelMode = null;
      let apparelFrameKey = '';

      function applyApparelFrame(mode, frame, regionDocBottom, contentDocTop) {
        img.style.width = `${frame.width}px`;
        img.style.height = `${frame.height}px`;
        img.style.zIndex = '3';
        img.style.maxWidth = 'none';

        if (mode === 'pinned') {
          img.style.position = 'fixed';
          img.style.top = '0px';
          img.style.left = '0px';
          img.style.transform = `translate3d(${frame.x}px, ${FINAL_TOP}px, 0)`;
        } else {
          const desiredDocTop = mode === 'ride-in'
            ? contentDocTop
            : regionDocBottom - frame.height;
          /* Anchor in the absolute containing block by measurement: park at
             top:0/left:0, read where that lands in document coords, then
             translate by the delta. Runs only on mode/size changes. */
          img.style.position = 'absolute';
          img.style.top = '0px';
          img.style.left = '0px';
          img.style.transform = 'none';
          const base = img.getBoundingClientRect();
          const dx = frame.x - base.left;
          const dy = desiredDocTop - (base.top + window.scrollY);
          img.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
        }

        if (innerImg) {
          innerImg.style.width = `${frame.width}px`;
          innerImg.style.height = `${frame.height}px`;
          /* AAS-5168: no objectPosition override. A `center -15%·height`
             shift (from 24e63b7) pushed the contained image up and clipped
             the top of the product photo (hood/collar cut next to the
             Story copy). The CSS `object-position: center top` keeps the
             whole photo visible, anchored to the frame top. */
          innerImg.style.objectPosition = '';
        }
      }

      function apparelUpdate() {
        if (window.innerWidth < DESKTOP_BP) {
          img.style.display = 'none';
          apparelVisible = false;
          apparelMode = null;
          return;
        }
        img.style.display = '';

        const regionRect = getRegionRect();
        const full = getFinalFrame();
        const frame = {
          width: full.width * APPAREL_SCALE,
          height: full.height * APPAREL_SCALE,
          x: full.x + (full.width - full.width * APPAREL_SCALE) / 2,
        };

        /* Determine if any part of the region is in the viewport */
        const regionInView = regionRect.top < window.innerHeight && regionRect.bottom > 0;
        /* Only show when the about section top has scrolled up enough */
        const shouldShow = regionInView && regionRect.top <= window.innerHeight * 0.5;

        if (shouldShow) {
          /* Same clamp logic as before, now only to PICK the mode:
             pinned at FINAL_TOP, riding with the content top on the way in,
             riding with the region bottom on the way out (bottom wins). */
          const contentTopY = aboutContent
            ? aboutContent.getBoundingClientRect().top
            : regionRect.top;
          let mode = 'pinned';
          let y = FINAL_TOP;
          if (contentTopY > y) {
            mode = 'ride-in';
            y = contentTopY;
          }
          if (y + frame.height > regionRect.bottom) {
            mode = 'ride-out';
          }

          const frameKey = `${Math.round(frame.x)}:${Math.round(frame.width)}:${Math.round(frame.height)}:${window.innerHeight}`;
          if (mode !== apparelMode || frameKey !== apparelFrameKey) {
            apparelMode = mode;
            apparelFrameKey = frameKey;
            applyApparelFrame(
              mode,
              frame,
              regionRect.bottom + window.scrollY,
              contentTopY + window.scrollY
            );
          }

          if (!apparelVisible) {
            /* First frame: set position without transition, then fade in */
            img.style.transition = 'none';
            void img.offsetHeight; /* force reflow */
            img.style.transition = 'opacity 350ms ease';
            apparelVisible = true;
          }

          img.style.opacity = '1';
        } else {
          img.style.opacity = '0';
          if (apparelVisible) {
            apparelVisible = false;
          }
        }
      }

      function onApparelScroll() {
        if (!apparelRaf) {
          apparelRaf = requestAnimationFrame(() => {
            apparelUpdate();
            apparelRaf = null;
          });
        }
      }

      window.addEventListener('scroll', onApparelScroll, { passive: true, signal });
      window.addEventListener('resize', () => {
        apparelUpdate();
      }, { signal });
      window.addEventListener('load', apparelUpdate, { signal });
      window.addEventListener('pageshow', apparelUpdate, { signal });

      apparelUpdate();
      requestAnimationFrame(apparelUpdate);

      return; /* Skip the default boots/gear path entirely */
    }

    /* ================================================================
     * DEFAULT MODE (boots/gear) — smooth scroll-progress animation.
     *
     * The image interpolates continuously between its initial hero
     * position and its final about-section position based on how far
     * the about section has scrolled into view. No fixed-duration CSS
     * transitions — the image follows the scroll position directly.
     * ================================================================ */

    /* How many pixels of scrolling span the full 0→1 animation range.
     * A larger value makes the transition feel more gradual. */
    const SCROLL_RANGE_PX = 600;

    /* Easing: in-out quart for a slower, more deliberate ease at both ends —
     * a premium, cinematic glide rather than a linear slide. */
    const easeInOutQuart = t => t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;

    const lerp = (a, b, t) => a + (b - a) * t;

    const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

    let raf = null;
    let isBottomLocked = false;
    let lastAppliedKey = '';
    /* AAS-5056 slew-rate limiter state (default template only): the last
     * applied hero top and the scrollY it was applied at. Late loads (lazy
     * images below the fold) shift the measured geometry mid-scroll — first
     * pass only — and the direct mapping would teleport the hero to the new
     * frame. The limiter caps per-frame movement to what the scroll itself
     * explains, so corrections glide in instead of jumping. */
    let slewY = null;
    let slewScrollY = null;
    let slewRaf = null;

    /* ── Gallery-overlay freeze (AAS-5075) ─────────────────────────────
     * Opening the fullscreen gallery scroll-locks the body, which zeroes
     * scrollY. Left running, the choreography would re-target the top
     * frame behind the overlay and visibly REPLAY the hero zoom (and
     * shift the pinned story copy) when the overlay closes. While the
     * backdrop carries .is-open every frame source (update + spring) is
     * frozen; on the close edge — the overlay restores the pre-open
     * scroll offset before our next frame — geometry is re-measured and
     * the frame snapped instantly, so nothing moves and the overlay's own
     * fade is the only visible transition. */
    let overlayBackdrop = null;
    let overlayWasOpen = false;

    function galleryOverlayOpen() {
      if (!overlayBackdrop) overlayBackdrop = document.querySelector('.pdp-apparel-overlay__backdrop');
      return !!overlayBackdrop && overlayBackdrop.classList.contains('is-open');
    }

    /* ── Ski hi-res zoom layer (AAS-5075) ──────────────────────────────
     * The base hero srcset is bounded at height=2000 (pdp-hero variant) —
     * fine at rest, visibly soft once the morph scales the image toward 3
     * viewport heights. data-zoom-src (main-product.liquid) carries the
     * unbounded-master Aprimo URL; on the first morphed frame it loads into
     * a second stacked <img> and crossfades in when decoded (CSS
     * .product__single-image-img--zoom, section-main-product.css). The
     * low-res layer stays underneath so there is never a blank frame. */
    /* AAS-5543: the zoom layer is the one image on this page with no <picture>
     * to gate on resolution, so main-product.liquid emits both qualities and
     * the choice happens here. Same media query the rest of the split uses —
     * devicePixelRatio would drift from it on the browsers the -webkit
     * alternative exists for. Falls back to the standard URL when the retina
     * twin is absent, which is the legacy native branch.
     *
     * The attribute is `data-zoom-src-retina`, not `-2x`: dataset only folds a
     * hyphen away when a lowercase letter follows it, so `data-zoom-src-2x`
     * stays reachable as `zoomSrc-2x` and dot notation never finds it. */
    const wantsRetina =
      typeof matchMedia === 'function' &&
      matchMedia('(min-resolution: 2dppx), (-webkit-min-device-pixel-ratio: 2)').matches;
    const zoomSrc = isSki && img
      ? ((wantsRetina && img.dataset.zoomSrcRetina) || img.dataset.zoomSrc || null)
      : null;
    let zoomImg = null;
    let zoomRequested = false;

    /* ── Ski soft-sticky story copy (AAS-5075) ─────────────────────────
     * JS-driven replacement for position: sticky, which pins and releases
     * with hard velocity steps. Each frame the copy's NATURAL flow top
     * (measured minus our own transform) is soft-clamped: a smooth-max
     * eases it into the viewport-centred pin, and — only once phase 2 is
     * running — a smooth-min against the image's live bottom edge eases it
     * back out the moment that edge enters the viewport. Both corners are
     * rounded over SKI_STORY_SOFT_PX, so velocity ramps instead of
     * stepping. The phase-2 gate is safe because at the hand-off the
     * image bottom is far below the fold, i.e. the release clamp is
     * inactive when it switches on. */
    const storyLayout = isSki && about ? about.querySelector('.product-about__layout') : null;
    const storyContainer = isSki && about ? about.querySelector('.product-about') : null;
    // Product info sidebar — the pinned story text must keep a clearance
    // below it on narrow screens (see the pin computation). AAS-5075
    const storyInfoSidebar = isSki ? document.querySelector('.product__info-wrapper') : null;
    let storyPinned = false;
    // Reveal state machine handle — assigned by the story-reveal block below,
    // driven per frame from updateStoryPin. AAS-5075
    let storyRevealApi = null;
    // Phase-2 early-pin glide (AAS-5075): from zoom completion the copy stops
    // following the scroll — it fixes where it is and time-eases to the pin;
    // on the way back out it eases back into the flow. null = inactive.
    let storyGlideV = null;
    // Keep-alive frame handle: the glide advances per update() frame, but the
    // rAF loop dies once the image spring settles — without this the copy
    // could freeze short of the centre when the user stops scrolling.
    let storyGlideRaf = null;
    // Exit ramp captured when phase 2 is left upward with the copy on screen:
    // { shift: rendered − flow at the crossing, natural: the flow position it
    // was captured at }. The shift decays to zero over
    // SKI_STORY_EXIT_DECAY_PX of upward scroll (see currentExitShift) — a
    // CONSTANT offset could never leave the viewport, and a time-glide
    // chased the receding flow and lurched when scrolling stopped.
    let storyExit = null;

    // Scroll-driven decaying exit offset; null when no exit ramp is active.
    // Clears itself once fully decayed (the copy is back in flow).
    function currentExitShift(naturalTop) {
      if (!storyExit) return null;
      const travelled = naturalTop - storyExit.natural;
      const k = Math.max(0, Math.min(1, 1 - travelled / SKI_STORY_EXIT_DECAY_PX));
      if (k === 0) {
        storyExit = null;
        return null;
      }
      return storyExit.shift * k;
    }

    function clearStoryPin() {
      if (!storyLayout) return;
      storyLayout.style.transform = '';
      storyLayout.style.position = '';
      storyLayout.style.top = '';
      storyLayout.style.left = '';
      storyLayout.style.width = '';
      storyLayout.style.paddingRight = '';
      storyInlineKey = '';
      storyPinned = false;
    }

    /* Horizontal placement (AAS-5075): centre the story block in the space
     * RIGHT of the zoomed image when there is room — otherwise keep the
     * flex-end right alignment. Applied as padding-right on the layout
     * (shifts label + copy together, works identically in flow and fixed
     * modes). Only recomputed when the cheap inputs change; the measuring
     * pass clears the padding first so it reads the pure flex-end position. */
    let storyInlineKey = '';
    function updateStoryInlineOffset(frame) {
      if (!storyLayout || !aboutContent || !frame) return;
      const imageRight = frame.x + frame.width;
      const key = `${window.innerWidth}|${Math.round(imageRight)}`;
      if (key === storyInlineKey) return;
      storyInlineKey = key;
      storyLayout.style.paddingRight = '';
      const block = aboutContent.getBoundingClientRect();
      const room = window.innerWidth - imageRight - block.width;
      const currentGap = window.innerWidth - block.right;
      /* Biased 20px right of true centre — a touch more air against the
       * picture reads better (design call). Clamps to the flex-end fallback
       * the same way. */
      const extra = Math.max(0, room / 2 - currentGap - 20);
      storyLayout.style.paddingRight = extra > 0.5 ? `${extra}px` : '';
    }

    /* C1 clamps with COMPACT support: quadratic blend inside ±soft, EXACT
     * clamp beyond it. The earlier sqrt smooth-max only converged to the
     * pin asymptotically, so the "pinned" copy kept creeping a few px
     * against the scroll direction for the whole hold. */
    function softMax(a, b, soft) {
      const d = a - b;
      if (d >= soft) return a;
      if (d <= -soft) return b;
      return b + ((d + soft) * (d + soft)) / (4 * soft);
    }

    function softMin(a, b, soft) {
      const d = a - b;
      if (d <= -soft) return a;
      if (d >= soft) return b;
      return b + d - ((d + soft) * (d + soft)) / (4 * soft);
    }

    /* Soft-sticky (AAS-5075), fixed-first. Two failed shapes taught us the
     * constraints: (1) an in-flow element corrected by a per-frame
     * transform is a tug-of-war — the compositor scrolls it, the rAF pulls
     * it back — so it wiggles; (2) mode-switching AT the corners jumps,
     * because that's where the two positioning systems disagree by the
     * async-scroll delta. So: the ENTIRE managed range — ease-in, hold,
     * ease-out — runs as position: fixed with the position owned 100% by
     * this rAF (same frame, same scrollY as the image morph, so copy and
     * skis stay mutually in sync). During the hold v is exactly `pin`
     * (compact-support clamps), so nothing is written and the fixed
     * element is compositor-stable. The element returns to flow only far
     * OUTSIDE the corners, where it moves at full scroll speed and the
     * hand-off positions coincide exactly. */
    function updateStoryPin(imageBottomViewport, phase2) {
      if (!storyLayout || !storyContainer) return;
      if (window.innerWidth < DESKTOP_BP) {
        clearStoryPin();
        return;
      }
      updateStoryInlineOffset(getFinalFrame());
      const rect = storyContainer.getBoundingClientRect();
      const naturalTop = rect.top;
      let pin = Math.max(SKI_STORY_MIN_TOP, (window.innerHeight - storyLayout.offsetHeight) / 2);
      /* Narrow screens: the reveal fires right as the zoom completes, when
       * the product info sidebar can still be on screen above the story —
       * keep the pinned text at least 50px below its bottom edge. The
       * sidebar scrolls away 1:1, so this floor slides the text up until
       * the centred pin takes over — continuous, no corner. */
      if (storyInfoSidebar) {
        const infoBottom = storyInfoSidebar.getBoundingClientRect().bottom;
        if (infoBottom > -50) pin = Math.max(pin, infoBottom + 50);
      }
      const soft = SKI_STORY_SOFT_PX;
      /* Reveal choreography (AAS-5075): the copy must NOT be seen scrolling
       * in — it materialises the moment the ZOOM COMPLETES (phase 2 start),
       * or at the latest when it reaches the pin zone, then rides the
       * soft-sticky into the centre. Driven here, not by an
       * IntersectionObserver, so it shares this frame's exact geometry. */
      const revealWasVisible = !!(storyRevealApi && storyRevealApi.isVisible());
      // The re-arm check needs the RENDERED top — with the exit offset the
      // block draws above its flow position, and re-arming by flow geometry
      // fired while it was still on screen (visible pop).
      const exitShift = currentExitShift(naturalTop);
      const renderedTop = naturalTop + (exitShift !== null ? exitShift : 0);
      if (storyRevealApi) storyRevealApi.update(naturalTop, pin + soft, phase2, renderedTop);
      // Once the reveal re-arms below the fold, drop the exit ramp — the
      // hidden block snaps back to its true flow position invisibly.
      if (storyRevealApi && !storyRevealApi.isVisible()) storyExit = null;
      let v = softMax(naturalTop, pin, soft);
      /* Phase-2 early pin (AAS-5075): the reveal starts at zoom completion
       * and the copy must already be FIXED then — not still riding the flow
       * until the scroll delivers it. From phase 2 the copy pins where it is
       * and GLIDES to the pin target (time-eased per frame); min() keeps it
       * continuous with the scroll-driven soft-max, whichever is closer to
       * the pin. Leaving phase 2 upward eases back into the flow the same
       * way, so there is no snap at the boundary in either direction. */
      if (phase2) {
        if (storyGlideV === null) {
          /* Engaging while the copy is still HIDDEN (the reveal fires this
           * same frame): place it AT the pin instantly — nothing visible can
           * jump, and the line reveal then plays at its final, centred
           * position instead of animating twice (rise + glide). A re-engage
           * with the copy already on screen glides from its RENDERED spot
           * (flow + any live exit offset). */
          storyGlideV = revealWasVisible ? renderedTop : pin;
        }
        storyExit = null;
        storyGlideV += (pin - storyGlideV) * 0.12;
        if (Math.abs(storyGlideV - pin) < 0.5) storyGlideV = pin;
        v = Math.min(v, storyGlideV);
      } else if (storyGlideV !== null) {
        /* Leaving phase 2 upward with the copy on screen: capture the exit
         * ramp — the offset then decays over SKI_STORY_EXIT_DECAY_PX of
         * upward scroll, so the copy scrolls away slightly faster than the
         * page and is guaranteed to leave the viewport. */
        storyExit = { shift: storyGlideV - naturalTop, natural: naturalTop };
        storyGlideV = null;
      }
      /* Only while actually gliding — once settled at the pin (=== pin) or
       * handed back to the flow (null), no extra frames are scheduled. */
      if (storyGlideV !== null && storyGlideV !== pin && storyGlideRaf === null) {
        storyGlideRaf = requestAnimationFrame(() => {
          storyGlideRaf = null;
          if (!signal.aborted) update();
        });
      }
      let releaseMax = Infinity;
      if (phase2) {
        releaseMax = pin + imageBottomViewport - window.innerHeight;
        v = softMin(v, releaseMax, soft);
      }
      const beforeZone = naturalTop >= pin + soft && storyGlideV === null;
      const afterZone = phase2 && releaseMax <= pin - soft;
      if (beforeZone || afterZone) {
        if (storyPinned) {
          storyPinned = false;
          storyLayout.style.position = '';
          storyLayout.style.top = '';
          storyLayout.style.left = '';
          storyLayout.style.width = '';
        }
        /* After full release the copy rides in flow with a CONSTANT offset
         * (v and naturalTop both move 1:1), so the compositor scrolls it
         * natively; the decaying phase-2 exit offset rides here on the way
         * back up; before the pin it is untouched. */
        const shift = afterZone ? v - naturalTop : exitShift !== null ? exitShift : 0;
        storyLayout.style.transform =
          Math.abs(shift) > 0.5 ? `translate3d(0, ${shift}px, 0)` : '';
      } else {
        if (!storyPinned) {
          storyPinned = true;
          storyLayout.style.position = 'fixed';
          storyLayout.style.top = '0px';
        }
        storyLayout.style.left = `${rect.left}px`;
        storyLayout.style.width = `${rect.width}px`;
        storyLayout.style.transform = `translate3d(0, ${v}px, 0)`;
      }
    }

    function ensureZoomLayer() {
      if (zoomRequested || !zoomSrc || !innerImg) return;
      zoomRequested = true;
      const hi = document.createElement('img');
      hi.className = 'product__single-image-img product__single-image-img--zoom';
      hi.alt = innerImg.alt || '';
      hi.decoding = 'async';
      hi.style.width = innerImg.style.width;
      hi.style.height = innerImg.style.height;
      hi.src = zoomSrc;
      img.appendChild(hi);
      zoomImg = hi;
      const reveal = () => hi.classList.add('is-loaded');
      if (hi.decode) {
        hi.decode().then(reveal).catch(reveal);
      } else {
        hi.onload = reveal;
      }
    }

    /* Breakpoint-crossing hygiene (AAS-5075): the abort-only teardown installed
     * at the top of init() kills the listeners but leaves this run's DOM
     * artefacts behind — so every mobile↔desktop crossing re-ran init() and
     * stacked ANOTHER hi-res zoom <img> over the hero, each frozen at its last
     * animated size ("several clones of the image"). Extend the teardown to
     * also remove every zoom layer and release the fixed-position story pin.
     * (querySelectorAll, not the zoomImg handle, so any stray layer from an
     * earlier broken run is swept up too.) */
    const abortOnlyTeardown = _teardown;
    _teardown = () => {
      abortOnlyTeardown();
      clearStoryPin();
      img.querySelectorAll('.product__single-image-img--zoom').forEach((n) => n.remove());
    };

    /* Ski hero opens the gallery on click (AAS-5075): the whole image is a
     * pointer target, delegating to the existing overlay button so there is
     * exactly ONE open path — the button also stays the accessible control
     * (the hero adds no tab stop or role). Clicks on the zoom layer bubble
     * up to the hero div, so both layers are covered. */
    if (isSki && document.querySelector('.pdp-apparel-overlay__btn')) {
      img.classList.add('product__single-image--clickable');
      // Re-queried per click — the overlay script can re-parent/rebuild its
      // trigger after this init runs, and a captured node would go stale.
      img.addEventListener(
        'click',
        () => document.querySelector('.pdp-apparel-overlay__btn')?.click(),
        { signal }
      );
    }

    /* ── Ski story reveal (AAS-5075) ───────────────────────────────────
     * The copy must never be SEEN scrolling in. Lifecycle:
     *   HIDDEN  — approaching in flow, masked away
     *   REVEAL  — the moment the zoom completes (or the pin zone is
     *             reached), already FIXED at the centred pin: headline
     *             lines rise out of overflow-hidden masks (staggered top
     *             to bottom), label/extras fade alongside
     *   HOLD    — pinned centre while the user scrolls through the image
     *   RELEASE — phase 2 lets it scroll away revealed (down), or the
     *             decaying exit ramp rides it out (up)
     *   RE-ARM  — once fully below the fold it resets silently, so every
     *             top-down pass replays the reveal.
     * Driven per frame from updateStoryPin (storyRevealApi) — no
     * IntersectionObserver, so it shares the morph's exact geometry.
     *
     * Lines are computed at runtime — wrap words, group by offsetTop,
     * rebuild as mask/text span pairs — because the copy is merchant data
     * and its wrapping depends on the viewport. Resize / fonts.ready
     * re-split and re-apply the CURRENT state without animating (pa-no-anim
     * guard). Skipped under reduced motion — no-JS and reduced-motion both
     * keep the plain, always-visible markup (every hiding rule is gated on
     * JS-added classes). */
    const storyRevealCleanup = (() => {
      const el = isSki && about ? about.querySelector('.product-about__description-text') : null;
      if (!el || !storyLayout || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

      const originalHTML = el.innerHTML;
      const esc = (s) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      let visible = false;
      let lineEls = [];

      function split() {
        el.innerHTML = originalHTML;
        // The copy arrives as <p>s (rich text) OR as a single
        // .metafield-multi_line_text_field <span> (metafield_tag) — resolve
        // whichever exists; bare text falls back to the container itself.
        let paras = [...el.querySelectorAll('p')];
        if (!paras.length) paras = [...el.querySelectorAll('[class*="metafield-"]')];
        if (!paras.length) paras = [el];
        paras.forEach((p) => {
          // Only rebuild text-and-<br> content — richer markup (links, bold)
          // is left intact rather than risking a mangled rebuild.
          if (![...p.children].every((c) => c.tagName === 'BR')) return;
          const parts = [];
          p.childNodes.forEach((n) => {
            if (n.nodeType === Node.TEXT_NODE) {
              n.textContent.trim().split(/\s+/).filter(Boolean).forEach((w) => parts.push({ w }));
            } else if (n.tagName === 'BR') {
              parts.push({ br: true });
            }
          });
          if (!parts.some((x) => x.w)) return;
          // Measurement pass: word spans with the <br>s preserved, so forced
          // breaks land as new offsetTop rows and group like natural wraps.
          p.innerHTML = parts
            .map((x) => (x.br ? '<br>' : `<span class="pa-reveal-word">${esc(x.w)}</span>`))
            .join(' ');
          const lines = [];
          let top = null;
          p.querySelectorAll('.pa-reveal-word').forEach((w) => {
            if (top === null || Math.abs(w.offsetTop - top) > 2) {
              lines.push([]);
              top = w.offsetTop;
            }
            lines[lines.length - 1].push(w.textContent);
          });
          // Joined with a space so textContent keeps word boundaries at line
          // breaks (copy-paste / screen readers) — between block spans the
          // whitespace collapses visually.
          p.innerHTML = lines
            .map(
              (ws) =>
                `<span class="product-about__reveal-line"><span class="product-about__reveal-text">${esc(ws.join(' '))}</span></span>`
            )
            .join(' ');
        });
        lineEls = [...el.querySelectorAll('.product-about__reveal-text')];
      }

      // Line stagger — top line first. Only the reveal ever animates (the
      // exit is a plain scroll-away and the re-arm runs under pa-no-anim),
      // so there is no reverse-direction variant.
      function setDelays() {
        lineEls.forEach((t, i) => {
          t.style.transitionDelay = `${i * 90}ms`;
        });
      }

      function setVisible(next) {
        if (next === visible) return;
        visible = next;
        setDelays();
        el.classList.toggle('is-revealed', next);
        storyLayout.classList.toggle('is-story-in', next);
      }

      // Re-split (line breaks changed) and re-apply the CURRENT state with
      // transitions suppressed for a frame, so a resize never replays the
      // animation.
      function resplit() {
        if (!el.isConnected) return;
        el.classList.add('pa-no-anim');
        storyLayout.classList.add('pa-no-anim');
        split();
        setDelays();
        void el.offsetHeight;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            el.classList.remove('pa-no-anim');
            storyLayout.classList.remove('pa-no-anim');
          })
        );
      }

      split();
      el.classList.add('product-about__description-text--reveal');
      storyLayout.classList.add('pa-story-reveal');

      // Silent re-arm: the up-scroll exit keeps the copy revealed and simply
      // lets it SCROLL away (no reverse playback — reversing while un-pinned
      // meant the fade happened off screen). Once the layout sits fully below
      // the fold it is reset to hidden without animating, so the next
      // down-scroll replays the reveal at the pin.
      function rearmHidden() {
        if (!visible) return;
        el.classList.add('pa-no-anim');
        storyLayout.classList.add('pa-no-anim');
        setVisible(false);
        void el.offsetHeight;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            el.classList.remove('pa-no-anim');
            storyLayout.classList.remove('pa-no-anim');
          })
        );
      }

      storyRevealApi = {
        // Called each frame from updateStoryPin. threshold = pin + soft (the
        // same boundary the pin state machine uses to engage).
        update(naturalTop, threshold, zoomDone, renderedTop) {
          // Show as soon as the image zoom completes (zoomDone = phase 2
          // boundary) — the copy fades in wherever it is and the soft-sticky
          // carries it into the pin; the pin-zone threshold stays as the
          // fallback for any path that reaches the pin without phase 2.
          if (!visible && (zoomDone || naturalTop <= threshold)) setVisible(true);
          // Re-arm the moment the RENDERED top passes the fold (the exit
          // offset draws the block above its flow position, so flow geometry
          // would re-arm while it is still on screen). The whole block is
          // below the viewport then, so the reset is invisible, and ANY
          // up-scroll that pushes the copy out re-arms the replay.
          else if (visible && (renderedTop ?? naturalTop) > window.innerHeight + 10) rearmHidden();
        },
        // Read by updateStoryPin BEFORE update() each frame — tells an
        // invisible engage (snap straight to the pin) from a visible one
        // (glide from the current spot).
        isVisible() {
          return visible;
        },
      };

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => resplit());
      }
      window.addEventListener('resize', resplit, { signal });

      return () => {
        storyRevealApi = null;
        el.innerHTML = originalHTML;
        el.classList.remove('product-about__description-text--reveal', 'is-revealed', 'pa-no-anim');
        storyLayout.classList.remove('pa-story-reveal', 'is-story-in', 'pa-no-anim');
      };
    })();

    if (storyRevealCleanup) {
      const teardownBeforeReveal = _teardown;
      _teardown = () => {
        teardownBeforeReveal();
        storyRevealCleanup();
      };
    }

    function getInitialDimensions() {
      if (usesPanelMorph && skiInitialTop !== null) {
        const { height } = getInitialBand();
        return { width: height * IMAGE_ASPECT_RATIO, height };
      }
      const height = window.innerHeight * INITIAL_HEIGHT_VH;
      const width = height * IMAGE_ASPECT_RATIO;
      return { width, height };
    }

    function getResponsiveInitialLeft() {
      const { width: initialWidth } = getInitialDimensions();
      const viewportWidth = window.innerWidth;
      const availableWidth = viewportWidth - sidebarWidth;
      const centeredLeft = (availableWidth - initialWidth) / 2;
      const minLeft = EDGE_GUTTER;

      // Ski: centered in the real free area — from the viewport's left edge
      // to the side panel's measured left edge (fallback: the 419px shell).
      // Deliberately UNclamped: the box is a near-square with transparent
      // padding around the narrow ski cutout, and it's sized off the viewport
      // HEIGHT — on tall screens it outgrows the area, and a left-gutter
      // clamp would shove the visible skis right of the area's center. Let
      // the transparent edges overflow symmetrically instead.
      if (usesPanelMorph && skiInitialTop !== null) {
        const areaRight = skiAreaRight !== null ? skiAreaRight : availableWidth;
        return (areaRight - initialWidth) / 2;
      }

      const threshold = 1600;
      if (viewportWidth > threshold) {
        return Math.max(minLeft, centeredLeft);
      } else {
        const factor = Math.min(1, (threshold - viewportWidth) / (threshold - DESKTOP_BP));
        return centeredLeft * (1 - factor) + minLeft * factor;
      }
    }

    /* Compute scroll progress t (0–1) driven by the about section's
     * viewport position. Animation starts when regionRect.top reaches
     * the bottom of the initial hero image and completes SCROLL_RANGE_PX
     * of scrolling later. */
    function getScrollProgress(regionRect) {
      const { height: initialHeight } = getInitialDimensions();
      /* scrollStart: regionRect.top value at which animation begins */
      let scrollStart = getInitialTopValue() + initialHeight;
      /* scrollEnd: regionRect.top value at which animation finishes */
      let scrollEnd = scrollStart - SCROLL_RANGE_PX;

      /* Ski: the morph spans the whole panel scroll — it starts with the
       * first scrolled pixel (t=0 at scrollY 0) and completes exactly when
       * the panel's bottom crosses the viewport top. In regionRect.top space:
       * start = regionTopDoc (the rect value at scroll 0), end = regionTopDoc
       * − panel bottom. Guarded so a degenerate measurement can't put the
       * end at/after the start. */
      if (usesPanelMorph && skiPanelBottom !== null && skiPanelBottom > 100) {
        const regionTopDoc = regionRect.top + window.scrollY;
        const earlyStart = regionTopDoc;
        const panelAwayEnd = regionTopDoc - skiPanelBottom;
        if (panelAwayEnd < earlyStart - 100) {
          scrollStart = earlyStart;
          scrollEnd = panelAwayEnd;
        }
      }

      const rawT = (scrollStart - regionRect.top) / (scrollStart - scrollEnd);
      return clamp(rawT, 0, 1);
    }

    function applyAtProgress(t, regionRect) {
      const easedT = easeInOutQuart(t);

      // Kick the hi-res layer off the first actually-morphed frame — the
      // resting hero stays on the light srcset until the user engages.
      if (t > 0.01) ensureZoomLayer();

      const { width: iw, height: ih } = getInitialDimensions();
      const ix = getResponsiveInitialLeft();
      const final = getFinalFrame();

      let x = lerp(ix, final.x, easedT);
      // Ski's frame carries its own y (vertically centered); others use the
      // classic FINAL_TOP. Explicit fallback — final.y must never be
      // undefined here (lerp would NaN the transform and the browser drops it).
      const finalTopY = final.y !== undefined ? final.y : FINAL_TOP;
      let y = lerp(getInitialTopValue(), finalTopY, easedT);
      let width = lerp(iw, final.width, easedT);
      let height = lerp(ih, final.height, easedT);

      /* Bottom-lock: once fully in the about section, clamp so the image
       * never overflows the region bottom as the user continues scrolling.
       * Ski opts out: its hero STAYS at the final frame (position: fixed)
       * and the sections after the story (and the footer) stack above it
       * with solid backgrounds (see section-product-about.css) — they
       * scroll over and cover it. */
      if (isDefaultTpl || isApparelFallback) {
        /* AAS-5466: the apparel triptych-fallback takes this same monotone
         * lock, but anchored on the REGION bottom (About+Datasheet+Materials),
         * not the About section's own bottom. The real apparel template rides
         * the shared packshot across the whole region ("riding with the region
         * bottom on the way out" — apparelUpdate above), so the fallback must
         * stay pinned through the datasheet too and only re-join the flow at
         * the region's end, just above Core Technologies.
         *
         * AAS-5056: after its vertical glide the default-template hero must
         * re-join the flow and scroll away with the page — but NOT via the
         * t>=1 hysteresis lock below, and NOT against regionRect. The region
         * spans About+Datasheet+Materials, so its bottom is screens further
         * down — clamped to it, the fixed hero lingered mid-viewport OVER the
         * datasheet content; and gated on t>=1, a fast fling let the edge
         * overtake the still-gliding spring and the hero jumped when the lock
         * finally engaged. Clamp against the About SECTION's own bottom, from
         * the first frame: a monotone min() follows that edge 1:1 —
         * continuous, stateless, no jump at any fling speed. */
        const lockEdge = isApparelFallback
          ? regionRect.bottom
          : about.getBoundingClientRect().bottom;
        const lockBottom = lockEdge - (isReservedAbout ? RESERVED_BOTTOM_GAP : 0);
        y = Math.min(y, lockBottom - height);
        /* Slew-rate limit: allow 1.5× the scroll delta plus 6px per frame.
         * Normal frames stay inside the allowance and map directly (zero
         * steady-state lag, no spring); a geometry correction exceeding it
         * (late image load moving the About edge) glides in at ≈360px/s,
         * driven by a keep-alive rAF until it converges. */
        const scrollNow = window.scrollY;
        // Reduced motion: corrections snap instead of gliding — the limiter
        // is itself a (subtle) animation.
        if (slewY !== null && !reduceMotionMq.matches) {
          const allowance = Math.abs(scrollNow - (slewScrollY === null ? scrollNow : slewScrollY)) * 1.5 + 6;
          const delta = y - slewY;
          if (Math.abs(delta) > allowance) {
            y = slewY + Math.sign(delta) * allowance;
            if (slewRaf === null) {
              slewRaf = requestAnimationFrame(() => {
                slewRaf = null;
                if (!signal.aborted) update();
              });
            }
          }
        }
        slewY = y;
        slewScrollY = scrollNow;
      } else if (t >= 1 && !usesPanelMorph) {
        const lockBottom = regionRect.bottom - (isReservedAbout ? RESERVED_BOTTOM_GAP : 0);
        const overflowBottom = (y + height) - lockBottom;

        if (!isBottomLocked && overflowBottom >= 0) {
          isBottomLocked = true;
        } else if (isBottomLocked && overflowBottom <= -BOTTOM_LOCK_HYSTERESIS) {
          isBottomLocked = false;
        }

        if (isBottomLocked) {
          y = y - Math.max(0, overflowBottom);
        }
      } else if (t < 1) {
        isBottomLocked = false;
      }

      /* Ski phase 2 (AAS-5075): the morph completes exactly when the panel
       * bottom crosses the viewport top (scrollY == skiPanelBottom, see
       * getScrollProgress). Scrolling past that point moves the pinned
       * image up with the page — the user scrolls THROUGH the zoomed
       * image; updateSkiScrollRoom sizes the story section so the next
       * sections arrive only after its bottom edge + SKI_SCROLL_END_PAD.
       * The first SKI_PHASE2_BLEND_PX ramp the follow in quadratically
       * (velocity 0→1) so the hand-off from the eased morph — whose end
       * velocity is 0 — has no visible corner; past the blend the shift is
       * the plain 1:1 overshoot minus the constant blend/2. */
      if (isSki && skiPanelBottom !== null && skiPanelBottom > 100) {
        const overshoot = window.scrollY - skiPanelBottom;
        if (overshoot > 0) {
          const blend = SKI_PHASE2_BLEND_PX;
          y -= overshoot < blend
            ? (overshoot * overshoot) / (2 * blend)
            : overshoot - blend / 2;
        }
      }

      /* Soft-sticky runs every frame, BEFORE the dedup below — its input is
       * the copy's scroll position, which moves even when the image frame
       * doesn't. Uses the post-shift y, i.e. the image's real rendered
       * bottom. */
      if (isSki) {
        const phase2 =
          skiPanelBottom !== null && skiPanelBottom > 100 && window.scrollY > skiPanelBottom;
        updateStoryPin(y + height, phase2);
      }

      const key = `${Math.round(x)}|${Math.round(y)}|${Math.round(width)}|${Math.round(height)}`;
      if (key === lastAppliedKey) return;
      lastAppliedKey = key;

      img.style.transition = 'none';
      img.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      img.style.width = `${width}px`;
      img.style.height = `${height}px`;
      // Ski sits BELOW the Product Story section (z:3 + transparent bg in
      // section-product-about.css) so the copy reads over the image.
      // AAS-5466: the apparel triptych-fallback takes z:3 like the real
      // apparel template — at z:2 the datasheet/materials sections (z:2,
      // solid bg, later in DOM) painted over the pinned packshot while it
      // rides the region. About (z:3, later in DOM) still reads over it.
      img.style.zIndex = t > 0 ? (usesPanelMorph && !isApparelFallback ? '2' : '3') : '';
      /* Panel-morph (ski/boot/protection): always 'none' — at t=0 the stock
         max-width:100% clamps the box to the viewport (the computed width is
         wider on tall screens), silently breaking centering and aspect.
         Remaining direct-mapped templates keep the at-rest clamp: the 84vh
         frame relies on max-width:100% to stay out of the sidebar on tall
         narrow windows. */
      img.style.maxWidth = usesPanelMorph ? 'none' : (t > 0 ? 'none' : '');

      if (innerImg) {
        innerImg.style.width = `${width}px`;
        innerImg.style.height = `${height}px`;
      }
      if (zoomImg) {
        zoomImg.style.width = `${width}px`;
        zoomImg.style.height = `${height}px`;
      }
    }

    function resetToMobile() {
      img.style.width = '';
      img.style.height = '';
      img.style.transform = '';
      img.style.transition = '';
      img.style.zIndex = '';
      img.style.maxWidth = '';
      if (innerImg) {
        innerImg.style.width = '';
        innerImg.style.height = '';
      }
      if (zoomImg) {
        zoomImg.style.width = '';
        zoomImg.style.height = '';
      }
      clearStoryPin();
      isBottomLocked = false;
      lastAppliedKey = '';
      stopSpring();
      springT = null;
      slewY = null;
      slewScrollY = null;
      if (slewRaf !== null) {
        cancelAnimationFrame(slewRaf);
        slewRaf = null;
      }
    }

    /* ── Ski elastic follow (AAS-1179) ─────────────────────────────────
     * The applied progress chases the scroll-derived target through a
     * mildly under-damped spring: the image trails the scroll slightly and
     * settles with a soft overshoot when scrolling stops — the "elastic"
     * feel. Critical damping would be 2·√stiffness ≈ 21; 17 leaves a small
     * bounce. The loop keeps running after the last scroll event until the
     * spring converges. Ski + boot + protection; reduced motion / resize /
     * load snap instantly. */
    const SKI_SPRING_STIFFNESS = 110;
    const SKI_SPRING_DAMPING = 17;
    const reduceMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    let springT = null;
    let springV = 0;
    let springRaf = null;
    let springLastTs = null;

    function springStep(ts) {
      springRaf = null;
      if (signal.aborted || window.innerWidth < DESKTOP_BP) return;
      if (galleryOverlayOpen()) {
        // Frozen under the overlay — drop velocity so the glide cannot
        // resume with stale momentum on close. update() owns the resync.
        overlayWasOpen = true;
        springV = 0;
        springLastTs = null;
        return;
      }

      const dt = Math.min(
        0.05,
        springLastTs !== null ? (ts - springLastTs) / 1000 : 1 / 60
      );
      springLastTs = ts;

      const regionRect = getRegionRect();
      const target = getScrollProgress(regionRect);
      if (springT === null) springT = target; // first frame: no glide-in

      springV += ((target - springT) * SKI_SPRING_STIFFNESS - springV * SKI_SPRING_DAMPING) * dt;
      springT = clamp(springT + springV * dt, 0, 1);
      applyAtProgress(springT, regionRect);

      if (Math.abs(target - springT) > 0.0004 || Math.abs(springV) > 0.002) {
        springRaf = requestAnimationFrame(springStep);
      } else {
        springT = target;
        springV = 0;
        springLastTs = null;
      }
    }

    function kickSpring() {
      if (springRaf === null) {
        springLastTs = null;
        springRaf = requestAnimationFrame(springStep);
      }
    }

    function stopSpring() {
      if (springRaf !== null) {
        cancelAnimationFrame(springRaf);
        springRaf = null;
      }
      springV = 0;
      springLastTs = null;
    }

    function update(instant = false) {
      if (galleryOverlayOpen()) {
        overlayWasOpen = true;
        return;
      }
      if (overlayWasOpen) {
        /* Close edge: the overlay restored the pre-open scroll offset, and a
         * fixed-position overlay cannot have moved the flow — so the cached
         * metrics still describe exactly the frame the user left. Snap to it
         * verbatim (no re-measure: refreshing here can pick up unrelated
         * post-load layout drift and land the hero a few px off, which reads
         * as a jump). A genuine viewport change while the overlay was open
         * re-enters through onResize's dimension check. */
        overlayWasOpen = false;
        stopSpring();
        lastAppliedKey = '';
        instant = true;
      }

      if (window.innerWidth < DESKTOP_BP) {
        resetToMobile();
        return;
      }

      /* AAS-5056: the default/accessory template maps the scroll position
       * directly — no spring, no trailing bounce. Its hero is meant to read
       * as an in-flow element (vertical glide + hard About-bottom clamp),
       * and an elastic lag on top of that read as wobble. */
      if (instant) {
        // Deliberate snap (resize reflow): bypass the slew limiter — gliding
        // 500px of viewport change at 360px/s would look like drift.
        slewY = null;
        slewScrollY = null;
      }
      if (usesPanelMorph && !isDefaultTpl && !reduceMotionMq.matches && !instant) {
        kickSpring();
        return;
      }

      stopSpring();
      const regionRect = getRegionRect();
      const t = getScrollProgress(regionRect);
      springT = t;
      applyAtProgress(t, regionRect);
    }

    function onScroll() {
      if (!raf) {
        raf = requestAnimationFrame(() => {
          update();
          raf = null;
        });
      }
    }

    // Last viewport size a resize was processed at. The overlay's body
    // scroll-lock toggles the scrollbar, firing resize events whose net
    // viewport change is zero — re-measuring on those picks up unrelated
    // layout drift and nudges the settled frame. Same-size resizes carry no
    // new information; skip them.
    let lastResizeW = window.innerWidth;
    let lastResizeH = window.innerHeight;

    function onResize() {
      if (galleryOverlayOpen()) {
        overlayWasOpen = true;
        return;
      }
      if (window.innerWidth === lastResizeW && window.innerHeight === lastResizeH) return;
      lastResizeW = window.innerWidth;
      lastResizeH = window.innerHeight;
      if (window.innerWidth < DESKTOP_BP) {
        img.style.display = 'none';
        updateSkiScrollRoom();
        return;
      }
      img.style.display = '';
      lastAppliedKey = '';
      refreshSkiMetrics();
      updateSkiScrollRoom();
      // Geometry changed — snap to the new frame, no elastic glide.
      update(true);
    }

    // Late layout shifts (fonts/images) move the sku anchor — re-measure
    // before repositioning.
    function onSettle() {
      lastAppliedKey = '';
      refreshSkiMetrics();
      updateSkiScrollRoom();
      /* AAS-4918: only hard-snap while still at the top (initial framing). Once
       * the user has scrolled into the panel-morph, a late load/pageshow settle
       * must re-target through the spring instead — an instant snap here lands as
       * a one-off jump on the first fast scroll when `load` fires mid-glide. */
      const scrolled = (window.scrollY || 0) > 1;
      update(usesPanelMorph && scrolled ? false : true);
    }

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onResize, { signal });
    window.addEventListener('load', onSettle, { signal });
    window.addEventListener('pageshow', onSettle, { signal });
    /* The phase-2 scroll room depends on the hero's decoded natural aspect
     * (getFinalFrame) — re-settle once it lands if it wasn't ready here. */
    if (isSki && innerImg && !innerImg.complete) {
      innerImg.addEventListener('load', onSettle, { signal });
    }

    updateSkiScrollRoom();
    /* First application must be SYNCHRONOUS (instant=true). The panel-morph
     * path otherwise defers to the spring's first rAF — one frame late. On
     * initial load that was invisible (the image wasn't decoded yet), but on
     * a variant media swap (pdp:media-swapped re-init) the fresh hero node
     * is pre-decoded and would paint one frame at its raw CSS frame — huge,
     * then snapping into place. Applying in the same task as the swap means
     * the correct geometry is there before the browser ever paints. */
    update(true);
    // rAF re-run: late layout shifts right after (re)init — still a snap,
    // never an elastic glide from a stale frame.
    requestAnimationFrame(() => update(true));
  }

  function getBaseFinalDimensions() {
    const vh = window.innerHeight;
    /* Target height for the morphed-in hero. FINAL_HEIGHT_VH (1.25) intentionally
     * scales the product shot up, but it must never exceed the viewport — an
     * over-viewport height makes the boot overflow the fold (the "huge boot"
     * reported on Safari, where the fixed image visibly spills past the screen).
     * Clamp to the viewport minus a small gutter so the hero always fits on
     * screen regardless of rendering engine. */
    const maxHeight = vh - EDGE_GUTTER * 2;
    const target = Math.max(vh * FINAL_HEIGHT_VH, vh * INITIAL_HEIGHT_VH, FINAL_HEIGHT_FALLBACK);
    const height = Math.min(target, maxHeight);
    const width = height * IMAGE_ASPECT_RATIO;
    return { width, height };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after a variant change: renderProductInfo() swaps the #ProductMedia
  // wrapper, destroying the .product__single-image node this controls and
  // inserting an unstyled fresh one. Without this the new node renders in normal
  // flow (visible, scrolling with the page) instead of the fixed, hidden-until-
  // scrolled cutout. init() tears down the prior run first, so no listener stacks.
  //
  // Driven by `pdp:media-swapped`, dispatched by theme.js INSIDE the swap callback
  // (right after the new node is committed) — reliable even under View Transitions,
  // unlike PUB_SUB variantChange which can fire before the async swap.
  document.addEventListener('pdp:media-swapped', init);

  // Re-init when the desktop breakpoint is crossed. Below DESKTOP_BP init parks
  // the hero with an inline display:none and returns immediately, without
  // binding any listener — so a window grown past the breakpoint kept the hero
  // hidden until a reload (the PDP simply had no image on desktop). Clearing the
  // inline display and the managed-node guard makes init() treat it as a fresh
  // node and run the desktop path; going the other way, init() re-applies the
  // hidden state itself.
  window.matchMedia(`(min-width: ${DESKTOP_BP}px)`).addEventListener('change', () => {
    const hero = document.querySelector('.product__single-image');
    if (hero) hero.style.display = '';
    _managedNode = null;
    init();
  });
})();
